import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, Upload } from 'lucide-react';
import MaterialAutocomplete from '../common/MaterialAutocomplete';
import { parseGaebXml, serializeForChat } from '../../services/gaebParser';

const PDF_PROCESSOR_URL = process.env.REACT_APP_PDF_PROCESSOR_URL || 'http://localhost:8000';

// Material type options for asphalt layers
const MATERIAL_OPTIONS = [
  'AC 16 T S', 'AC 22 T N', 'AC 22 T S', 'AC 32 T N', 'AC 32 T S', 'AC 16 TD',
  'AC 11 B N', 'AC 16 B N', 'AC 16 B S', 'AC 22 B S', 'AC 5 D L', 'AC 8 D N',
  'AC 8 D S', 'AC 11 D N', 'AC 11 D S', 'AC 8 D SP', 'AC 11 D SP', 'AC 16 D S',
  'SMA 5 N', 'SMA 5 S', 'SMA 8 N', 'SMA 8 S', 'SMA 11 S', 'MA 5 N', 'MA 5 S',
  'MA 8 N', 'MA 8 S', 'MA 11 N', 'MA 11 S', 'PA 8', 'PA 11', 'PA 16', 'SMA 16 B S',
  'SMA 22 B S', 'AC 16 B S SG', 'AC 22 B S SG'
];

const EMPTY_ENTRY = { material: '', flaeche: '', dicke: '', dichte: '' };

const LayerAreasModal = ({
  isOpen,
  onClose,
  projectId,
  onSave,
  initialData = null,
  isLoading = false
}) => {
  const [deckschichten, setDeckschichten] = useState([{ ...EMPTY_ENTRY }]);
  const [binderschichten, setBinderschichten] = useState([{ ...EMPTY_ENTRY }]);
  const [tragschichten, setTragschichten] = useState([{ ...EMPTY_ENTRY }]);
  const [quantitySource, setQuantitySource] = useState('deliveryNotes');
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const gaebFileInputRef = useRef(null);

  // Helpers
  const calculateMenge = (flaeche, dicke, dichte) => {
    const f = parseFloat(flaeche) || 0;
    const d = parseFloat(dicke) || 0;
    const r = parseFloat(dichte) || 0;
    return (f === 0 || d === 0 || r === 0) ? 0 : (f * d * r) / 100;
  };

  const dbEntryToForm = (e) => ({
    material: e.material || '',
    flaeche: e.flaeche ? String(e.flaeche) : '',
    dicke: e.dicke ? String(e.dicke) : '',
    dichte: e.dichte ? String(e.dichte) : ''
  });

  // Reset form when modal opens/closes or initial data changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setDeckschichten(
          initialData.deckschichten?.length
            ? initialData.deckschichten.map(dbEntryToForm)
            : [{ ...EMPTY_ENTRY }]
        );
        setBinderschichten(
          initialData.binderschichten?.length
            ? initialData.binderschichten.map(dbEntryToForm)
            : [{ ...EMPTY_ENTRY }]
        );
        setTragschichten(
          initialData.tragschichten?.length
            ? initialData.tragschichten.map(dbEntryToForm)
            : [{ ...EMPTY_ENTRY }]
        );
        setQuantitySource(initialData.quantitySource || 'deliveryNotes');
      } else {
        setDeckschichten([{ ...EMPTY_ENTRY }]);
        setBinderschichten([{ ...EMPTY_ENTRY }]);
        setTragschichten([{ ...EMPTY_ENTRY }]);
        setQuantitySource('deliveryNotes');
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  // Entry mutators
  const updateEntry = (setter, index, field, value) => {
    setter(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e));
    // Clear error
    const errKey = `${setter.name}_${index}_${field}`;
    if (errors[errKey]) setErrors(prev => ({ ...prev, [errKey]: '' }));
  };

  const addEntry = (setter) => setter(prev => [...prev, { ...EMPTY_ENTRY }]);

  const removeEntry = (setter, index) =>
    setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

  // GAEB upload handler
  const handleGaebUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsExtracting(true);
    setExtractionError(null);

    try {
      const xmlText = await file.text();
      const parsedData = parseGaebXml(xmlText);
      const context = serializeForChat(parsedData);

      const res = await fetch(`${PDF_PROCESSOR_URL}/api/gaeb/extract-layers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ boq_summary: context.boqSummary }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error ${res.status}`);
      }

      const data = await res.json();
      if (!data.success || !data.layers) {
        throw new Error('Keine Schichtdaten in der Antwort gefunden.');
      }

      const l = data.layers;
      const toEntries = (arr) =>
        arr.filter(e => e.material || e.flaeche || e.dicke || e.dichte)
           .map(e => ({
             material: e.material || '',
             flaeche: e.flaeche ? String(e.flaeche) : '',
             dicke: e.dicke ? String(e.dicke) : '',
             dichte: e.dichte ? String(e.dichte) : '2.4'
           }));

      const ds = toEntries(l.deckschichten || []);
      const bs = toEntries(l.binderschichten || []);
      const ts = toEntries(l.tragschichten || []);

      if (ds.length) setDeckschichten(ds);
      if (bs.length) setBinderschichten(bs);
      if (ts.length) setTragschichten(ts);
    } catch (err) {
      console.error('GAEB extraction error:', err);
      setExtractionError(err.message);
    } finally {
      setIsExtracting(false);
      if (gaebFileInputRef.current) gaebFileInputRef.current.value = '';
    }
  };

  // Validation — Deckschicht & Binderschicht first entries required
  const validateForm = () => {
    const newErrors = {};

    const validateEntries = (entries, prefix, requireFirst) => {
      entries.forEach((entry, idx) => {
        const isFirst = idx === 0;
        ['flaeche', 'dicke'].forEach(field => {
          const val = entry[field]?.toString().trim();
          if (requireFirst && isFirst && !val) {
            newErrors[`${prefix}_${idx}_${field}`] = 'Erforderlich';
          } else if (val) {
            const num = parseFloat(val);
            if (isNaN(num)) newErrors[`${prefix}_${idx}_${field}`] = 'Ungültige Zahl';
            else if (num < 0) newErrors[`${prefix}_${idx}_${field}`] = 'Muss positiv sein';
          }
        });
      });
    };

    validateEntries(deckschichten, 'deck', true);
    validateEntries(binderschichten, 'binder', true);
    validateEntries(tragschichten, 'trag', false);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const toDb = (entries) =>
        entries
          .filter(e => e.flaeche || e.dicke || e.material || e.dichte)
          .map(e => ({
            material: e.material || '',
            flaeche: parseFloat(e.flaeche) || 0,
            dicke: parseFloat(e.dicke) || 0,
            dichte: parseFloat(e.dichte) || 0
          }));

      await onSave({
        deckschichten: toDb(deckschichten),
        binderschichten: toDb(binderschichten),
        tragschichten: toDb(tragschichten),
        quantitySource
      });
      onClose();
    } catch (error) {
      console.error('Error saving layer areas:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDeckschichten([{ ...EMPTY_ENTRY }]);
    setBinderschichten([{ ...EMPTY_ENTRY }]);
    setTragschichten([{ ...EMPTY_ENTRY }]);
    setQuantitySource('deliveryNotes');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{projectId}</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={isSaving}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* GAEB Auto-fill Section */}
        <div className="mb-6 p-4 border-2 border-dashed border-green-300 rounded-lg bg-green-50">
          <div className="flex items-center gap-3">
            <input
              ref={gaebFileInputRef}
              type="file"
              accept=".x83,.X83,.xml"
              onChange={handleGaebUpload}
              className="hidden"
              disabled={isExtracting || isSaving}
            />
            <button
              onClick={() => gaebFileInputRef.current?.click()}
              disabled={isExtracting || isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExtracting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Wird analysiert...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>GAEB-Datei hochladen</span>
                </>
              )}
            </button>
            <span className="text-sm text-gray-600">
              X83-Datei hochladen, um Schichtdaten automatisch zu befüllen
            </span>
          </div>
          {extractionError && (
            <p className="mt-2 text-sm text-red-600">Fehler: {extractionError}</p>
          )}
        </div>

        <div className="space-y-6 mb-6 max-h-[60vh] overflow-y-auto pr-1">
          {[
            { title: 'Deckschicht', entries: deckschichten, setter: setDeckschichten, prefix: 'deck' },
            { title: 'Binderschicht', entries: binderschichten, setter: setBinderschichten, prefix: 'binder' },
            { title: 'Tragschicht', entries: tragschichten, setter: setTragschichten, prefix: 'trag' }
          ].map(({ title, entries, setter, prefix }) => (
            <div key={prefix}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <button
                  type="button"
                  onClick={() => addEntry(setter)}
                  disabled={isSaving}
                  className="text-sm text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                >
                  + Hinzufügen
                </button>
              </div>
              {entries.map((entry, idx) => (
                <div key={idx} className="mb-2">
                  <div className="grid gap-3 items-end" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1.5fr auto' }}>
                    <div>
                      {idx === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Material</label>}
                      <MaterialAutocomplete
                        value={entry.material}
                        onChange={(v) => updateEntry(setter, idx, 'material', v)}
                        options={MATERIAL_OPTIONS}
                        placeholder="Material auswählen"
                        disabled={isSaving}
                      />
                    </div>
                    <div>
                      {idx === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Fläche (m²)</label>}
                      <input
                        type="number" step="0.01" min="0"
                        value={entry.flaeche}
                        onChange={(e) => updateEntry(setter, idx, 'flaeche', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors[`${prefix}_${idx}_flaeche`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Fläche"
                        disabled={isSaving}
                      />
                      {errors[`${prefix}_${idx}_flaeche`] && (
                        <p className="text-red-500 text-xs mt-0.5">{errors[`${prefix}_${idx}_flaeche`]}</p>
                      )}
                    </div>
                    <div>
                      {idx === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Dicke (cm)</label>}
                      <input
                        type="number" step="0.1" min="0"
                        value={entry.dicke}
                        onChange={(e) => updateEntry(setter, idx, 'dicke', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors[`${prefix}_${idx}_dicke`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Dicke"
                        disabled={isSaving}
                      />
                      {errors[`${prefix}_${idx}_dicke`] && (
                        <p className="text-red-500 text-xs mt-0.5">{errors[`${prefix}_${idx}_dicke`]}</p>
                      )}
                    </div>
                    <div>
                      {idx === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Dichte (t/m³)</label>}
                      <input
                        type="number" step="0.01" min="0"
                        value={entry.dichte}
                        onChange={(e) => updateEntry(setter, idx, 'dichte', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Dichte"
                        disabled={isSaving}
                      />
                    </div>
                    <div>
                      {idx === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Menge (t)</label>}
                      <input
                        type="text"
                        value={calculateMenge(entry.flaeche, entry.dicke, entry.dichte).toFixed(0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                        readOnly
                      />
                    </div>
                    <div>
                      {idx === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">&nbsp;</label>}
                      {entries.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEntry(setter, idx)}
                          disabled={isSaving}
                          className="text-red-400 hover:text-red-600 p-2 disabled:opacity-50"
                          title="Entfernen"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {isSaving ? (
          <div className="flex flex-col items-center justify-center py-4 mb-6">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
            <p className="text-gray-700">Daten werden gespeichert...</p>
          </div>
        ) : (
          <div className="flex justify-end gap-4">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isSaving}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              disabled={isSaving}
            >
              Speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LayerAreasModal;
